import { ContentModalContentType, ContentTypeForComparison } from 'domains/content/types';
import { Folder } from 'models/types';
import useUnarchiveContent from './mutations/useUnarchiveContent';
import { useQueryClient } from 'react-query';
import QueryKeys from './queries/keys';

type Props = {
    activeItem: ContentModalContentType | Folder;
    refetchTableData: () => void;
    resetActiveItem: () => void;
    setErrorMessage: (error: string) => void;
    setSuccessMessage: (message: string) => void;
};

const useUnarchiveTableItem = ({
    activeItem,
    refetchTableData,
    resetActiveItem,
    setErrorMessage,
    setSuccessMessage,
}: Props) => {
    const queryClient = useQueryClient();

    const onError = (error: Error) => {
        setErrorMessage(error.message);
        resetActiveItem();
    };

    const onSuccess = () => {
        setSuccessMessage(`"${activeItem?.name}" successfully unarchived.`);
        refetchTableData();
        resetActiveItem();

        // Invalidate content and archived content queries to refresh the lists
        queryClient.invalidateQueries(QueryKeys.CONTENT());
        queryClient.invalidateQueries(QueryKeys.ARCHIVED_CONTENT);
        // Also invalidate all content queries with different parameters to ensure both original and search queries are updated
        queryClient.invalidateQueries(['content']);
    };

    const getContentTypeForApi = (type: string): string => {
        switch (type) {
            case ContentTypeForComparison.EMAIL:
                return 'email';
            case ContentTypeForComparison.SMS:
                return 'sms';
            case ContentTypeForComparison.LANDING_PAGE:
                return 'landing_page';
            case ContentTypeForComparison.DIRECT_MAIL:
                return 'direct_mail';
            case ContentTypeForComparison.VOICE:
                return 'voice';
            default:
                return type.toLowerCase();
        }
    };

    const { isLoading, mutate: unarchiveContent, isSuccess } = useUnarchiveContent({
        onError,
        onSuccess,
    });

    const getUnarchiveItemFunction = () => {
        if (!activeItem) return () => {};

        const unarchiveData = {
            id: Number(activeItem.id),
            type: getContentTypeForApi(activeItem.type),
        };

        return () => unarchiveContent(unarchiveData);
    };

    return { getUnarchiveItemFunction, isLoading, isSuccess };
};

export default useUnarchiveTableItem;
